import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { ValidationError } from "../../errors";
import { attachment } from "../shared";
import { defineModel } from "../types";

const kling26MotionControlParams = z
  .object({
    prompt: z.string().default(""),
    characterOrientation: z.enum(["image", "video"]).default("video"),
    keepOriginalSound: z.boolean().default(true),
    attachments: z.array(attachment).optional(),
  })
  .strip();

export const kling26MotionControl = defineModel({
  key: "kling_2_6_motion_control",
  stepType: "video",
  spec: {
    label: "Kling 2.6 Motion Control",
    description:
      "Advanced motion control with reference image and video support",
    icon: "kling-icon.svg",
    tags: ["video-gen"],
    featured: true,
    info: "Generating a video with this model takes between 5-10 minutes.",
    features: {
      attachments: true,
      prompts: true,
      variations: true,
      characterOrientation: true,
    } as const,
    options: {
      attachments: [
        {
          type: "image",
          allowedFormats: ["image/jpeg", "image/png", "image/webp"],
          label: "Reference Image",
          slot: "reference_image",
          required: true,
        },
        {
          type: "video",
          allowedFormats: ["video/mp4", "video/mov"],
          label: "Reference Video",
          slot: "reference_video",
          required: true,
        },
      ],
      characterOrientation: {
        items: [
          {
            label: "Video",
            value: "video",
            icon: "video",
            description:
              "Orientation matches reference video - better for complex motions",
          },
          {
            label: "Image",
            value: "image",
            icon: "image",
            description:
              "Orientation matches reference image - better for camera movements",
          },
        ],
      },
    },
    params: kling26MotionControlParams,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/fal-ai/kling-video/v2.6/pro/motion-control",
    entries: [{ unit: "per_second", unitPriceMicrodollars: 112_000 }],
  },
  providers: [
    {
      provider: "runware",
      modelId: "klingai:kling-video@2.6-pro",
      estimateCost(params) {
        const videoAttachment = params.attachments?.find(
          (att) => att.slot === "reference_video",
        );
        const videoDurationMs = videoAttachment?.durationMs;
        const durationSeconds = videoDurationMs ? videoDurationMs / 1000 : 10;
        return 0.07 * 1_000_000 * durationSeconds * (1 + BRAND_MARKUP);
      },
      costPerSecond: 0.07 * 1_000_000 * (1 + BRAND_MARKUP),
      toPayload(params) {
        const getAttachment = (slot: string) =>
          params.attachments?.find((attachment) => attachment.slot === slot);

        const imageAttachment = getAttachment("reference_image");
        const videoAttachment = getAttachment("reference_video");
        const videoUrl = videoAttachment?.data;
        const imageUrl = imageAttachment?.data;

        if (!imageUrl) {
          throw new ValidationError(
            "Please connect a reference image to generate a video.",
          );
        }

        if (!videoUrl) {
          throw new ValidationError(
            "Please connect a reference video to generate a video.",
          );
        }

        if (!params.prompt?.length) {
          throw new ValidationError(
            "Please enter a prompt to generate a video.",
          );
        }

        // Runware rejects reference images whose width or height is outside
        // [340, 3850]. Surface a clear validation error before we hit the 400.
        const { width, height } = imageAttachment ?? {};
        if (width != null && height != null) {
          const RUNWARE_MIN = 340;
          const RUNWARE_MAX = 3850;
          const outOfRange =
            width < RUNWARE_MIN ||
            width > RUNWARE_MAX ||
            height < RUNWARE_MIN ||
            height > RUNWARE_MAX;
          if (outOfRange) {
            throw new ValidationError(
              `Reference image dimensions ${width}x${height} are outside Runware's supported range (340 to 3850 on each side). Please use an image within that range.`,
            );
          }
        }

        // Runware enforces a reference-video duration window that depends on
        // characterOrientation: [3, 10]s for "image", [3, 30]s for "video".
        // Out-of-range videos get HTTP 400 invalidDuration; surface it first.
        const videoDurationMs = videoAttachment?.durationMs;
        if (videoDurationMs != null) {
          const durationSeconds = videoDurationMs / 1000;
          const maxSeconds =
            (params.characterOrientation ?? "video") === "image" ? 10 : 30;
          if (durationSeconds < 3 || durationSeconds > maxSeconds) {
            throw new ValidationError(
              `Reference video must be 3 to ${maxSeconds} seconds long (yours is ${durationSeconds.toFixed(1)}s). Please trim it to that range.`,
            );
          }
        }

        return {
          model: this.modelId,
          positivePrompt: params.prompt,
          inputs: {
            referenceImages: [imageUrl],
            referenceVideos: [videoUrl],
          },
          providerSettings: {
            klingai: {
              keepOriginalSound: params.keepOriginalSound ?? true,
              characterOrientation: params.characterOrientation ?? "video",
            },
          },
        };
      },
    },
  ],
});
